// Libs
import React, { useEffect, useMemo, useState } from 'react'
// Styles
import './App.reset.scss'
import styles from './App.module.scss'
// Components
import Gallery from './components/Gallery'
import Footer from './components/Footer'
import Admin from './components/Admin'
// Site (owner customization) — composed into the base-library Footer container.
import Dock from './site/Dock'
import Nav from './site/Nav'
// Utils
import { AppContext } from './App.context'
import { useScreenSize } from './App.hook'
import { siteConfig } from './config/site.config'

function App () {

  // Keep the document title in sync with config — a no-op in production (the
  // static <title> is already build-injected to the same value) but it fixes the
  // raw %REACT_APP_SITE_TITLE% placeholder shown in dev (where there's no build).
  useEffect(() => {
    if (siteConfig.htmlTitle) document.title = siteConfig.htmlTitle
  }, [])

  // Global
  const screenSize = useScreenSize()
  const isInitialized = useMemo(() => {
    const { width, height } = screenSize
    return !!(width && height)
  }, [screenSize])

  // When the lightbox opens, the top header (inside Gallery) and the bottom dock
  // (Footer, a sibling) both retreat off-frame so the enlarged photo owns the
  // stage. Gallery owns the lightbox state, so it reports the flag up here and we
  // hand it to the Footer.
  const [lightboxOpen, setLightboxOpen] = useState(false)

  // Language override (set from the debug panel; null = follow the browser).
  const [langOverride, setLangOverride] = useState<string | null>(null)

  // Context — memoized so unrelated App re-renders (e.g. the lightbox toggle
  // above) don't churn the value and re-render every context consumer.
  const contextValue = useMemo(() => ({
    // Flags
    isInitialized,
    // Global
    screenSize,
    // i18n
    lang: langOverride ?? (navigator.language || 'en'),
    langOverride,
    setLangOverride,
  }), [isInitialized, screenSize, langOverride])

  // 隐藏路由：/admin 是站长后台（登录后上传/删除照片）。
  // 刻意不做成 react-router —— 整站就这一个额外入口，一个正则足够，
  // 也避免给引擎引入路由依赖。ESA Pages 配了 SPA 回退，直接访问 /admin 不会 404。
  const isAdminRoute = useMemo(
    () => /^\/admin(\/|$)/.test(window.location.pathname),
    [],
  )

  useEffect(() => {
    if (isAdminRoute) document.title = '相册后台'
  }, [isAdminRoute])

  if (isAdminRoute) return <Admin />

  return (
    <AppContext.Provider value={contextValue}>
      <div className={styles.app}>
        {/* 顶部站间导航：与博客站同一排菜单（首页 / 文章 / 碎碎念 / 相册 / 留言板 / 友链 / 关于） */}
        <Nav hidden={lightboxOpen}/>
        <Gallery onLightboxChange={setLightboxOpen}/>
        <Footer hidden={lightboxOpen}><Dock /></Footer>
        {/* 主题开关已按站长要求下线（站点配色改由系统深浅色偏好决定，见 public/index.html 的
            no-flash 脚本）。ThemeToggle 组件本身保留在源码里，想加回来直接渲染即可。 */}
        {/* 站长后台入口（右上角，很淡）。地址仍是 /admin，忘了就直接输。 */}
        <a
          className={`${styles.adminEntry}${lightboxOpen ? ` ${styles.adminEntryHidden}` : ''}`}
          href="/admin"
        >
          站长登录
        </a>
      </div>
    </AppContext.Provider>
  )
}

export default App
